// Reunderwriting Module (Tier 3) PR 4 — Time-to-completion metrics.
// Overall mean/median tiles + per-event-type table.

import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/design-system/components/ui";
import { useReunderwritingTimeToCompletionMetrics } from "@/hooks/reunderwriting/useReunderwriting";
import type { ReunderwritingMetricsFilter } from "@/types/reunderwriting";

interface Props {
  filter: ReunderwritingMetricsFilter;
}

export function TimeToCompletionSection({ filter }: Props) {
  const navigate = useNavigate();
  const q = useReunderwritingTimeToCompletionMetrics(filter);

  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState />;
  const { avgDaysOverall, medianDaysOverall, completedCaseCount, byEventType } = q.data;

  return (
    <div className="flex flex-col gap-space-4" data-testid="reunderwriting-metrics-ttc">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-space-3">
        <Tile label="Avg days" value={avgDaysOverall.toFixed(1)} />
        <Tile label="Median days" value={medianDaysOverall.toFixed(1)} />
        <Tile label="Completed cases" value={String(completedCaseCount)} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>By event type</CardTitle>
          <p className="text-small text-gray-mid mt-space-1">
            Click a row to drill through to the Pipeline filtered to that event type.
          </p>
        </CardHeader>
        <CardContent>
          {byEventType.length === 0 ? (
            <div className="text-small text-gray-mid">
              No completed cases in this window.
            </div>
          ) : (
            <table className="w-full text-small" aria-label="Time to completion by event type">
              <thead className="text-caption text-gray-mid text-left">
                <tr>
                  <th className="py-space-1">Event type</th>
                  <th className="text-right">Cases</th>
                  <th className="text-right">Avg days</th>
                </tr>
              </thead>
              <tbody>
                {byEventType.map((row) => (
                  <tr
                    key={row.bucket}
                    className="border-t border-border-light cursor-pointer hover:bg-white-warm"
                    onClick={() =>
                      navigate(`/reunderwriting/pipeline?eventTypeCode=${row.bucket}`)
                    }
                  >
                    <td className="py-space-2 font-mono">{row.bucket}</td>
                    <td className="py-space-2 text-right">{row.caseCount}</td>
                    <td className="py-space-2 text-right">{row.avgDays.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent>
        <div className="text-label uppercase tracking-label text-gray-mid">{label}</div>
        <div className="font-display text-h2 text-onyx">{value}</div>
      </CardContent>
    </Card>
  );
}

function Loading() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-gray-mid">
      Loading time-to-completion metrics…
    </div>
  );
}

function ErrorState() {
  return (
    <div className="flex h-64 items-center justify-center text-small text-status-error">
      Failed to load time-to-completion metrics.
    </div>
  );
}
